import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Container,
    Fade,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { authLogout } from "../redux/userRelated/userSlice";

function initialsFromName(name) {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Logout = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser, currentRole } = useSelector((state) => state.user);

    const displayName = currentUser?.name?.trim() || "Signed-in user";
    const roleLabel = currentUser?.role || currentRole || "";
    const subtitle =
        currentUser?.schoolName ||
        currentUser?.school?.schoolName ||
        currentUser?.teachSclass?.sclassName ||
        "";

    const initials = useMemo(() => initialsFromName(displayName), [displayName]);

    const handleLogout = () => {
        dispatch(authLogout());
        navigate("/platform");
    };

    const handleCancel = () => {
        navigate(-1);
    };

    return (
        <Box
            sx={{
                minHeight: "calc(100vh - 64px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: { xs: 4, sm: 6 },
                background: (theme) =>
                    theme.palette.mode === "light"
                        ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${theme.palette.grey[50]} 45%, ${alpha(
                              theme.palette.secondary.main,
                              0.06
                          )} 100%)`
                        : `linear-gradient(165deg, ${alpha(theme.palette.common.black, 0.35)} 0%, ${theme.palette.grey[900]} 50%)`,
            }}
        >
            <Container maxWidth="sm">
                <Fade in timeout={400}>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            overflow: "hidden",
                            border: (t) => `1px solid ${alpha(t.palette.divider, 0.12)}`,
                            boxShadow: (t) =>
                                t.palette.mode === "light"
                                    ? `0 24px 48px ${alpha(t.palette.common.black, 0.08)}, 0 0 1px ${alpha(t.palette.common.black, 0.06)}`
                                    : `0 24px 48px ${alpha(t.palette.common.black, 0.4)}`,
                        }}
                    >
                        <Box
                            sx={{
                                px: { xs: 2.5, sm: 4 },
                                py: { xs: 3, sm: 4 },
                                textAlign: "center",
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 72,
                                    height: 72,
                                    mx: "auto",
                                    mb: 2,
                                    fontSize: "1.35rem",
                                    fontWeight: 800,
                                    bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                                    color: "error.main",
                                    border: (t) => `2px solid ${alpha(t.palette.error.main, 0.25)}`,
                                }}
                            >
                                {initials}
                            </Avatar>

                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.2 }}>
                                Sign out
                            </Typography>
                            <Typography variant="h4" component="h1" fontWeight={800} gutterBottom sx={{ mt: 0.5, lineHeight: 1.2 }}>
                                {displayName}
                            </Typography>

                            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                {roleLabel ? (
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        sx={{
                                            px: 1.25,
                                            py: 0.35,
                                            borderRadius: 10,
                                            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                                            color: "primary.main",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {roleLabel}
                                    </Typography>
                                ) : null}
                                {subtitle ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
                                        {subtitle}
                                    </Typography>
                                ) : null}
                            </Stack>

                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 360, mx: "auto" }}>
                                You will need to sign in again to access your dashboard and school data.
                            </Typography>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    fullWidth
                                    startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
                                    onClick={handleCancel}
                                    sx={{ borderRadius: 2, fontWeight: 600, py: 1.25 }}
                                >
                                    Stay signed in
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="large"
                                    fullWidth
                                    startIcon={<LogoutOutlinedIcon />}
                                    onClick={handleLogout}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        py: 1.25,
                                        boxShadow: (t) => `0 8px 24px ${alpha(t.palette.error.main, 0.35)}`,
                                    }}
                                >
                                    Log out
                                </Button>
                            </Stack>
                        </Box>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
};

export default Logout;
